import { useState, type FormEvent } from "react";

import { ListFilter } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export interface ProductAdvancedFilters {
  published: string;
  stock: string;
  processing: string;
  moderation: string;
  multipleVariants: boolean;
  multipleOffers: boolean;
}

type ProductAdvancedSelectFilter = "published" | "stock" | "processing" | "moderation";

const EMPTY_PRODUCT_ADVANCED_FILTERS: ProductAdvancedFilters = {
  published: "all",
  stock: "all",
  processing: "all",
  moderation: "all",
  multipleVariants: false,
  multipleOffers: false,
};

interface ProductFiltersDialogProps {
  filters: ProductAdvancedFilters;
  onApply: (filters: ProductAdvancedFilters) => void;
  showModeration?: boolean;
}

export function ProductFiltersDialog({ filters, onApply, showModeration = true }: ProductFiltersDialogProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<ProductAdvancedFilters>(filters);
  const activeFilterCount = countActiveFilters(filters, showModeration);
  const draftFilterCount = countActiveFilters(draft, showModeration);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) setDraft(filters);
    setOpen(nextOpen);
  };

  const updateDraft = (key: ProductAdvancedSelectFilter, value: string) => {
    setDraft((current) => ({ ...current, [key]: value }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onApply(showModeration ? draft : { ...draft, moderation: "all" });
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant={activeFilterCount > 0 ? "secondary" : "outline"}
          size="sm"
          className="h-9 gap-2 px-3"
          aria-label={`Filters${activeFilterCount > 0 ? `, ${activeFilterCount} active` : ""}`}
        >
          <ListFilter aria-hidden="true" className="h-4 w-4" />
          <span className="hidden sm:inline">Filters</span>
          {activeFilterCount > 0 ? (
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground">
              {activeFilterCount}
            </span>
          ) : null}
        </Button>
      </DialogTrigger>

      <DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] max-w-2xl overflow-y-auto">
        <form className="space-y-6" onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Product filters</DialogTitle>
            <DialogDescription>
              Refine the catalogue by availability, workflow state, moderation, variants, or
              offers.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <DialogFilterSelect
              id="product-filter-published"
              label="Published"
              value={draft.published}
              onValueChange={(value) => updateDraft("published", value)}
              options={[
                { value: "all", label: "All products" },
                { value: "true", label: "Published" },
                { value: "false", label: "Unpublished" },
              ]}
            />
            <DialogFilterSelect
              id="product-filter-stock"
              label="Stock"
              value={draft.stock}
              onValueChange={(value) => updateDraft("stock", value)}
              options={[
                { value: "all", label: "Any stock status" },
                { value: "true", label: "In stock" },
                { value: "false", label: "Out of stock" },
              ]}
            />
            <DialogFilterSelect
              id="product-filter-processing"
              label="Processing"
              value={draft.processing}
              onValueChange={(value) => updateDraft("processing", value)}
              options={[
                { value: "all", label: "Any processing status" },
                { value: "completed", label: "Completed" },
                { value: "processing", label: "Processing" },
                { value: "pending", label: "Pending" },
                { value: "partial", label: "Partial" },
                { value: "failed", label: "Failed" },
              ]}
            />
            {showModeration ? <DialogFilterSelect
              id="product-filter-moderation"
              label="Moderation"
              value={draft.moderation}
              onValueChange={(value) => updateDraft("moderation", value)}
              options={[
                { value: "all", label: "Any moderation status" },
                { value: "approved", label: "Approved" },
                { value: "pending", label: "Pending" },
                { value: "high", label: "High severity" },
                { value: "medium", label: "Medium severity" },
                { value: "low", label: "Low severity" },
              ]}
            /> : null}

            <div className="space-y-3 sm:col-span-2">
              <DialogFilterCheckbox
                id="product-filter-multiple-variants"
                label="Multiple variants"
                checked={draft.multipleVariants}
                onCheckedChange={(checked) =>
                  setDraft((current) => ({ ...current, multipleVariants: checked }))
                }
              />
              <DialogFilterCheckbox
                id="product-filter-multiple-offers"
                label="Multiple offers"
                checked={draft.multipleOffers}
                onCheckedChange={(checked) =>
                  setDraft((current) => ({ ...current, multipleOffers: checked }))
                }
              />
            </div>
          </div>

          <DialogFooter className="gap-2 border-t pt-4 sm:space-x-0">
            <Button
              type="button"
              variant="ghost"
              className="sm:mr-auto"
              disabled={draftFilterCount === 0}
              onClick={() => setDraft(EMPTY_PRODUCT_ADVANCED_FILTERS)}
            >
              Clear filters
            </Button>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit">Apply filters</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function countActiveFilters(filters: ProductAdvancedFilters, showModeration: boolean): number {
  const selectFilterCount = [
    filters.published,
    filters.stock,
    filters.processing,
    showModeration ? filters.moderation : "all",
  ].filter((value) => value !== "all").length;

  return selectFilterCount + Number(filters.multipleVariants) + Number(filters.multipleOffers);
}

interface DialogFilterCheckboxProps {
  id: string;
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}

function DialogFilterCheckbox({
  id,
  label,
  checked,
  onCheckedChange,
}: DialogFilterCheckboxProps) {
  return (
    <div className="flex items-center gap-3 rounded-lg border p-3">
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(nextChecked) => onCheckedChange(nextChecked === true)}
      />
      <Label htmlFor={id} className="cursor-pointer font-normal">
        {label}
      </Label>
    </div>
  );
}

interface DialogFilterSelectProps {
  id: string;
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  options: ReadonlyArray<{ value: string; label: string }>;
}

function DialogFilterSelect({
  id,
  label,
  value,
  onValueChange,
  options,
}: DialogFilterSelectProps) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger id={id} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
